import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Banknote, ClipboardList, FileText, LayoutDashboard, Loader2,
  Printer, Search, ShoppingBag, Truck, Wallet,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/hooks/useTenant';
import { formatBRL } from '@/lib/format';

/**
 * Busca global com autocomplete: abas (rotas), pedidos de farm, pedidos ML,
 * contas, transações, fornecedores e farms — aceita nomes incompletos.
 * Selecionar um resultado navega para a aba correspondente.
 */

interface SearchResult {
  group: string;
  label: string;
  sublabel?: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
}

// Índice estático de abas/páginas
const PAGES: SearchResult[] = [
  { group: 'Abas', label: 'Visão geral (Financeiro)', path: '/financeiro',              icon: LayoutDashboard },
  { group: 'Abas', label: 'Contas',                   path: '/financeiro/contas',       icon: Wallet },
  { group: 'Abas', label: 'A pagar',                  path: '/financeiro/pagar',        icon: FileText },
  { group: 'Abas', label: 'A receber',                path: '/financeiro/receber',      icon: FileText },
  { group: 'Abas', label: 'Importar CSV',             path: '/financeiro/importar',     icon: FileText },
  { group: 'Abas', label: 'Conciliação',              path: '/financeiro/conciliacao',  icon: FileText },
  { group: 'Abas', label: 'DRE',                      path: '/financeiro/dre',          icon: FileText },
  { group: 'Abas', label: 'Tarefas',                  path: '/tarefas',                 icon: ClipboardList },
  { group: 'Abas', label: 'Logística (Mercado Livre)', path: '/logistica',              icon: Truck },
  { group: 'Abas', label: 'Estoque',                  path: '/logistica/estoque',       icon: ShoppingBag },
  { group: 'Abas', label: 'Atendimento',              path: '/logistica/atendimento',   icon: ShoppingBag },
  { group: 'Abas', label: 'Farms — Dashboard',        path: '/farms',                   icon: Printer },
  { group: 'Abas', label: 'Farms — Marketplace',      path: '/farms/marketplace',       icon: Printer },
  { group: 'Abas', label: 'Farms — Minhas farms',     path: '/farms/minhas',            icon: Printer },
  { group: 'Abas', label: 'Farms — Pedidos recebidos', path: '/farms/recebidos',        icon: Printer },
  { group: 'Abas', label: 'Farms — Meus pedidos',     path: '/farms/pedidos',           icon: Printer },
  { group: 'Abas', label: 'Farms — Conversas',        path: '/farms/conversas',         icon: Printer },
  { group: 'Abas', label: 'Configurações',            path: '/configuracoes',           icon: FileText },
  { group: 'Abas', label: 'Canais de venda',          path: '/configuracoes/canais',    icon: FileText },
  { group: 'Abas', label: 'Categorias (DRE)',         path: '/configuracoes/categorias', icon: FileText },
  { group: 'Abas', label: 'Fornecedores',             path: '/configuracoes/fornecedores', icon: FileText },
];

function norm(s: string): string {
  // Remove diacríticos (busca ignora acentos)
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

export function GlobalSearch() {
  const { tenantId } = useTenant();
  const navigate = useNavigate();
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = term.trim();
    if (q.length < 2) { setResults([]); setBusy(false); return; }

    debounceRef.current = setTimeout(async () => {
      setBusy(true);
      const nq = norm(q);
      const like = `%${q}%`;

      // 1) Abas (client-side, com acentos normalizados)
      const pages = PAGES.filter((p) => norm(p.label).includes(nq)).slice(0, 4);

      // 2) Entidades (RLS limita ao tenant do usuário)
      // Busca por número do pedido: aceita o código exibido (#A1B2C3D4) ou UUID
      const codeQ = q.replace(/[#\s]/g, '');
      const isCode = /^[0-9a-fA-F]{2,}$/.test(codeQ);
      const [accounts, ordersByCode, mlOrders, txs, farms, suppliers] = await Promise.all([
        supabase.from('accounts').select('id, name, institution, balance_cents').ilike('name', like).limit(4),
        isCode
          ? supabase.rpc('search_farm_orders_by_code' as never, { p_code: codeQ } as never)
          : Promise.resolve({ data: [] as any[] }),
        supabase.from('ml_orders').select('id, ml_order_id, buyer_nickname, total_amount_cents').or(`ml_order_id.ilike.${like},buyer_nickname.ilike.${like}`).limit(4),
        supabase.from('transactions').select('id, description, amount_cents, direction').ilike('description', like).limit(4),
        supabase.from('farms').select('id, name, slug').ilike('name', like).limit(4),
        supabase.from('payees').select('id, name').ilike('name', like).limit(3),
      ]);

      // farm_orders também por nome da farm (busca mais útil que por uuid)
      const { data: farmOrdersByName } = await supabase
        .from('farm_orders')
        .select('id, status, total_amount_cents, farms!inner(name)')
        .ilike('farms.name', like)
        .limit(4);

      const out: SearchResult[] = [
        ...pages,
        ...((accounts.data ?? []) as any[]).map((a) => ({
          group: 'Contas', label: a.name,
          sublabel: `${a.institution ?? 'Conta'} · ${formatBRL(a.balance_cents)}`,
          path: '/financeiro/contas', icon: Wallet,
        })),
        ...([
            ...((ordersByCode.data ?? []) as any[]).map((o) => ({ id: o.id, status: o.status, total_amount_cents: o.total_amount_cents, name: o.farm_name })),
            ...((farmOrdersByName ?? []) as any[]).map((o) => ({ id: o.id, status: o.status, total_amount_cents: o.total_amount_cents, name: o.farms?.name })),
          ])
          .filter((o, i, arr) => arr.findIndex((x) => x.id === o.id) === i)
          .map((o) => ({
            group: 'Pedidos (Farms)', label: `Pedido #${String(o.id).slice(0, 8).toUpperCase()}`,
            sublabel: `${o.name ?? 'Farm'} · ${o.status} · ${formatBRL(o.total_amount_cents)}`,
            path: `/farms/pedidos/${o.id}`, icon: Printer,
          })),
        ...((mlOrders.data ?? []) as any[]).map((o) => ({
          group: 'Pedidos (ML)', label: `#${o.ml_order_id}`,
          sublabel: `${o.buyer_nickname ?? 'Comprador'} · ${formatBRL(o.total_amount_cents)}`,
          path: '/logistica', icon: Truck,
        })),
        ...((txs.data ?? []) as any[]).map((t) => ({
          group: 'Transações', label: t.description,
          sublabel: `${t.direction === 'in' ? 'Entrada' : 'Saída'} · ${formatBRL(t.amount_cents)}`,
          path: '/financeiro/conciliacao', icon: Banknote,
        })),
        ...((farms.data ?? []) as any[]).map((f) => ({
          group: 'Farms', label: f.name, sublabel: 'Perfil da farm',
          path: `/farms/${f.slug}`, icon: Printer,
        })),
        ...((suppliers.data ?? []) as any[]).map((s) => ({
          group: 'Fornecedores', label: s.name, sublabel: 'Cadastro de fornecedor',
          path: '/configuracoes/fornecedores', icon: FileText,
        })),
      ];

      setResults(out.slice(0, 14));
      setHighlight(0);
      setBusy(false);
    }, 250);

    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [term, tenantId]);

  function go(r: SearchResult) {
    setOpen(false);
    setTerm('');
    navigate(r.path);
  }

  // Agrupa para exibição
  const grouped = results.reduce<Record<string, SearchResult[]>>((acc, r) => {
    (acc[r.group] ??= []).push(r);
    return acc;
  }, {});
  const flat = Object.values(grouped).flat();

  return (
    <div className="relative w-full" ref={boxRef}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-soft" />
      <input
        type="search"
        value={term}
        onChange={(e) => { setTerm(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => Math.min(h + 1, flat.length - 1)); }
          if (e.key === 'ArrowUp')   { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
          if (e.key === 'Enter' && flat[highlight]) { e.preventDefault(); go(flat[highlight]); }
          if (e.key === 'Escape') setOpen(false);
        }}
        placeholder="Buscar pedido, conta, aba, farm…"
        className="w-full pl-9 pr-3 py-2 rounded-xl border border-border bg-surface-subtle text-sm focus:bg-surface-raised focus:border-brand-400 outline-none"
      />

      {open && term.trim().length >= 2 && (
        <div className="absolute top-full mt-2 w-full card shadow-xl z-40 max-h-96 overflow-y-auto py-1">
          {busy && flat.length === 0 && (
            <p className="text-xs text-ink-muted flex items-center gap-2 px-4 py-3">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Buscando…
            </p>
          )}
          {!busy && flat.length === 0 && (
            <p className="text-xs text-ink-muted px-4 py-3">Nada encontrado para "{term}".</p>
          )}
          {Object.entries(grouped).map(([group, items]) => (
            <div key={group}>
              <p className="px-4 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-ink-soft">{group}</p>
              {items.map((r) => {
                const idx = flat.indexOf(r);
                const Icon = r.icon;
                return (
                  <button
                    key={`${r.path}-${r.label}`}
                    onClick={() => go(r)}
                    onMouseEnter={() => setHighlight(idx)}
                    className={`w-full flex items-center gap-3 px-4 py-2 text-left transition-colors ${
                      idx === highlight ? 'bg-brand-50' : ''
                    }`}
                  >
                    <Icon className="w-4 h-4 text-ink-muted shrink-0" />
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm text-ink truncate">{r.label}</span>
                      {r.sublabel && <span className="block text-xs text-ink-muted truncate">{r.sublabel}</span>}
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
